import { MapContainer, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "./ViewportMap3D.css";
import EnvironmentMetadataView from "../viewport3d/Viewport3DEnvironmentMetadata";
import React, { useEffect, useMemo } from "react";
import PiTileLayerComponent from "./PiTileLayer";
import { useSelector } from "react-redux";
import {
  selectEnvironmentInteractionPoints,
  selectMaxZoomLevel,
} from "../../app/slices/EnvironmentSlice";
import {
  SelectedTab,
  selectSelectedTab,
  selectViewportMapFocus,
} from "../../app/slices/GlobalSlice";
import { selectIsPlaying } from "../../app/slices/TrainingSlice";
import useDelay from "../../hooks/Delay.hook";
import ViewportMapMarkerScale from "./ViewportMapMarkerScale";

function ViewportMapBackgroundClickHandler(props: {
  onBackgroundClick?: () => void;
}) {
  useMapEvents({
    click: () => {
      props.onBackgroundClick?.();
    },
  });

  return null;
}

export default function ViewportMap3D(props: {
  visibleLayerIds?: string[];
  onBackgroundClick?: () => void;
}) {
  const { visibleLayerIds, onBackgroundClick } = { ...props };
  const maxZoomLevel = useSelector(selectMaxZoomLevel);
  const ref = React.useRef<L.Map | null>(null);
  const selectedTab = useSelector(selectSelectedTab);
  const isPlaying = useSelector(selectIsPlaying);
  const { delay } = useDelay();

  const interactionPoints = useSelector(selectEnvironmentInteractionPoints);
  const focusRequest = useSelector(selectViewportMapFocus);

  // fly to the requested map position (normalized x/y, like the markers use)
  useEffect(() => {
    if (focusRequest.requestId === 0 || !ref.current) return;
    const z = 512; // size
    const latLng = ref.current.unproject(
      [z * focusRequest.x, z * focusRequest.y],
      1
    );
    ref.current.flyTo(latLng, ref.current.getMaxZoom(), {
      duration: 0.4,
      easeLinearity: 0.25,
    });
  }, [focusRequest.requestId]);

  // bounds: makes sure you can't pan outside the world
  const b = L.latLngBounds([-90, -180], [90, 180]);

  useEffect(() => {
    if (maxZoomLevel !== undefined && selectedTab === SelectedTab.Timeline) {
      ref.current?.setMaxZoom(maxZoomLevel - 1);
    }
  }, [maxZoomLevel, ref.current, selectedTab]);

  useEffect(() => {
    updateZoomLevelAsync();
  }, [isPlaying]);

  const updateZoomLevelAsync = async () => {
    await delay(150);
    if (maxZoomLevel !== undefined && selectedTab === SelectedTab.Timeline) {
      ref.current?.setMaxZoom(maxZoomLevel - 1);
    }
  };

  const mapKey = useMemo(
    () => JSON.stringify(interactionPoints),
    [interactionPoints]
  );

  return (
    <MapContainer
      ref={ref}
      center={[180, 180]}
      zoom={2}
      worldCopyJump={false}
      minZoom={0}
      maxBoundsViscosity={1}
      zoomControl={false}
      attributionControl={false}
      maxBounds={b}
    >
      <ViewportMapMarkerScale />
      <ViewportMapBackgroundClickHandler
        onBackgroundClick={onBackgroundClick}
      />
      <PiTileLayerComponent urlTemplate={"/{z}/{x}_{y}.jpg"} />
      <EnvironmentMetadataView key={mapKey} visibleLayerIds={visibleLayerIds} />
    </MapContainer>
  );
}
